// The first row of the caster select: who the match is against, as a switch
// of three pills, Play, Invite, and Join, in the middle of the head. The
// chosen pill is filled yellow. Under it, in a room, the code in a field with
// Copy and Share, and in Join, the field that takes a code. In Play a line
// takes the place of the field. A line under the field says whether the
// friend is here. The field and the line have one height in every mode. Changed 2026-10-14. The three were
// words with a bar under the chosen one, after a label "Play against".
// Garrett: "create an obvious pill pattern nav centered in the top header
// ... its too subtle". Garrett, on an earlier draft: "we do need a container
// for the invite section/input itself so it LOOKS like an input". The words
// are plain placeholders.

import { GAME_NAME } from '../constants';
import type { CasterId } from '../content/casters';
import { el } from '../lib/dom';
import { otherSeat } from '../room/protocol';
import {
  chooseOpponent,
  currentOpponent,
  joinTypedCode,
  makeRoomAgain,
  type Opponent,
  opponentStore,
  typeCode,
  typedCode,
} from '../state/opponent';
import { type RoomState, roomStore } from '../state/roomStore';
import { createKeyIcon } from './keyIcons';

const LABEL = 'Play against';
const OPPONENT_WORDS: Record<Opponent, string> = { play: 'Play', invite: 'Invite', join: 'Join' };
const OPPONENTS: readonly Opponent[] = ['play', 'invite', 'join'];
const COPY_LABEL = 'Copy link';
const COPIED_LABEL = 'Copied';
const SHARE_LABEL = 'Share';
const JOIN_LABEL = 'Join';
const AGAIN_LABEL = 'Try again';
/** The field says what to type, so no line under it does. Garrett, 2026-10-14: "make ... the placeholder instead". */
const CODE_PLACEHOLDER = 'Type the code your friend sent';
/** In Play, in the place of the field. */
const PLAY_NOTE = 'Moon takes the other side on this screen';
const SHARE_TEXT = `Play ${GAME_NAME} with me`;
const COPIED_MS = 1500;

const linkOf = (code: string): string => `${window.location.origin}/${code}`;

/** What the line under the field says. */
const statusOf = (room: RoomState | null): string => {
  const { making, failed } = opponentStore.get();
  if (!room) {
    if (making) return 'Making a room…';
    if (failed) return 'No room was made.';
    return '';
  }
  if (room.status !== 'open' || !room.view) return 'Connecting…';
  if (room.seat === null) return 'You are watching';
  const friend = otherSeat(room.seat);
  if (!room.view.filled[friend]) return 'Waiting for your friend';
  if (room.view.awayMs[friend] !== null) return 'Your friend is away';
  if (room.view.ready[friend]) return 'Your friend is ready';
  return 'Your friend is here';
};

/** `picks` gives the casters picked so far, so they go to a room that is made or joined. */
export const createOpponentRow = (picks: () => readonly CasterId[]): HTMLElement => {
  const tabs = OPPONENTS.map((opponent) =>
    el('button', {
      className: 'opponent__tab',
      type: 'button',
      textContent: OPPONENT_WORDS[opponent],
      onclick: () => void chooseOpponent(opponent, picks()),
    }),
  );

  const code = el('span', { className: 'opponent__code' });
  const copyKey = el(
    'button',
    { className: 'opponent__key', type: 'button', ariaLabel: COPY_LABEL },
    [createKeyIcon('copy')],
  );
  copyKey.onclick = () => {
    const room = roomStore.get();
    if (!room) return;
    void navigator.clipboard
      ?.writeText(linkOf(room.code))
      .then(() => {
        copyKey.ariaLabel = COPIED_LABEL;
        copyKey.classList.add('opponent__key--done');
        window.setTimeout(() => {
          copyKey.ariaLabel = COPY_LABEL;
          copyKey.classList.remove('opponent__key--done');
        }, COPIED_MS);
      })
      .catch(() => {});
  };
  const shareKey = el(
    'button',
    {
      className: 'opponent__key',
      type: 'button',
      ariaLabel: SHARE_LABEL,
      hidden: typeof navigator.share !== 'function',
      onclick: () => {
        const room = roomStore.get();
        if (room)
          void navigator
            .share({ title: GAME_NAME, text: SHARE_TEXT, url: linkOf(room.code) })
            .catch(() => {});
      },
    },
    [createKeyIcon('share')],
  );
  // A room the server did not make: the field keeps its place, and a filled key asks again.
  const againKey = el('button', {
    className: 'opponent__again',
    type: 'button',
    textContent: AGAIN_LABEL,
    onclick: () => makeRoomAgain(picks()),
  });
  const shown = el('div', { className: 'opponent__field' }, [code, copyKey, shareKey, againKey]);

  const input = el('input', {
    className: 'opponent__input',
    type: 'text',
    maxLength: 4,
    placeholder: CODE_PLACEHOLDER,
    autocapitalize: 'characters',
    autocomplete: 'off',
    spellcheck: false,
    ariaLabel: 'Room code',
  });
  const goKey = el(
    'button',
    {
      className: 'opponent__key opponent__key--go',
      type: 'button',
      ariaLabel: JOIN_LABEL,
      onclick: () => joinTypedCode(picks()),
    },
    [createKeyIcon('go')],
  );
  input.addEventListener('input', () => typeCode(input.value));
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') joinTypedCode(picks());
  });
  const typing = el('div', { className: 'opponent__field' }, [input, goKey]);

  const note = el('p', { className: 'opponent__note', textContent: PLAY_NOTE });
  const status = el('p', { className: 'opponent__status' });
  const root = el('div', { className: 'opponent' }, [
    el('div', { className: 'opponent__head' }, [
      el('div', { className: 'opponent__tabs', role: 'group', ariaLabel: LABEL }, tabs),
    ]),
    el('div', { className: 'opponent__body' }, [note, shown, typing, status]),
  ]);

  const draw = (): void => {
    const room = roomStore.get();
    const now = currentOpponent();
    OPPONENTS.forEach((opponent, index) => {
      const tab = tabs[index];
      if (tab) tab.ariaPressed = String(opponent === now);
    });
    const { making, failed } = opponentStore.get();
    // Invite always has the field of the code: while the room is made, when it is made, and when it failed.
    shown.hidden = !room && now !== 'invite';
    code.textContent = room?.code ?? '····';
    copyKey.disabled = !room;
    shareKey.disabled = !room;
    const showAgain = !room && !making && failed;
    againKey.hidden = !showAgain;
    copyKey.hidden = showAgain;
    shareKey.hidden = showAgain || typeof navigator.share !== 'function';
    typing.hidden = room !== null || now !== 'join';
    // The line about Moon is for Play only.
    note.hidden = room !== null || now !== 'play';
    goKey.disabled = typedCode() === null;
    if (input.value !== opponentStore.get().typed) input.value = opponentStore.get().typed;
    status.textContent = statusOf(room);
    status.hidden = status.textContent === '';
  };

  draw();
  roomStore.subscribe(draw);
  opponentStore.subscribe(draw);
  return root;
};
